import { useState } from "react";
import { useNavigate } from "react-router";
import type { PotSize } from "@/types";
import { Button } from "@/app/components/system/Button";
import { LocationPicker } from "@/app/components/onboarding/LocationPicker";
import { PotIllustration } from "@/app/components/PotIllustration";
import { SunHoursPicker } from "@/app/components/SunHoursPicker";
import { CameraScanIllustration, CareTagIllustration, SpeciesSearchIllustration } from "@/app/components/AddActionIllustrations";
import { BackIcon, SparkleIcon } from "@/app/components/system/Icons";
import { useAuth } from "@/app/lib/auth-context";
import { usePots, useWeather } from "@/app/lib/data-context";
import { markOnboardingComplete } from "@/app/lib/onboarding";
import { isSunRangeInvalid } from "@/app/lib/sunHours";
import { POT_SIZE_ORDER, formatApproxVolumeLiters } from "@/app/lib/potSizes";

type OnboardingStep = "welcome" | "location" | "pot" | "plant";

const STEPS: OnboardingStep[] = ["welcome", "location", "pot", "plant"];

const PLACEMENTS = [
  { id: "balcony", label: "Balcony", name: "Balcony pots" },
  { id: "windowsill", label: "Windowsill", name: "Kitchen window" },
  { id: "garden", label: "Garden bed", name: "Garden bed" },
  { id: "indoors", label: "Indoors", name: "Indoor corner" },
] as const;

const BIG_ACTION_BUTTON =
  "w-full flex items-center gap-3.5 p-[18px] border rounded-[18px] cursor-pointer text-left mb-2.5 transition-all duration-150 hover:-translate-y-px bg-paper border-line text-ink hover:border-moss-2";
const BIG_ACTION_ILLU = "relative w-[70px] h-16 rounded-2xl bg-cream-2 grid place-items-center shrink-0 overflow-hidden";

/**
 * Post-signup onboarding wizard: location → first pot → optional first plant.
 * Skippable after location; location is required to continue past step 2.
 */
export function OnboardingScreen() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const { location, setLocation } = useWeather();
  const { addPot } = usePots();

  const [step, setStep] = useState<OnboardingStep>("welcome");
  const [placement, setPlacement] = useState<string | null>(null);
  const [potName, setPotName] = useState("");
  const [potSize, setPotSize] = useState<PotSize>("medium");
  const [sunStart, setSunStart] = useState("");
  const [sunEnd, setSunEnd] = useState("");
  const [showSunHours, setShowSunHours] = useState(false);
  const [createdPotId, setCreatedPotId] = useState<string | null>(null);
  const [savingPot, setSavingPot] = useState(false);

  const stepIndex = STEPS.indexOf(step);
  const sunInvalid = showSunHours && isSunRangeInvalid(sunStart || null, sunEnd || null);

  function finish(destination = "/") {
    if (user) markOnboardingComplete(user.id);
    navigate(destination, { replace: true });
  }

  function goToPlantStep(potId: string) {
    setCreatedPotId(potId);
    setStep("plant");
  }

  async function savePotAndContinue() {
    if (sunInvalid) return;
    const name = potName.trim();
    if (!name) return;
    setSavingPot(true);
    const pot = await addPot({
      name,
      shape: "terracotta",
      size: potSize,
      sunStart: showSunHours && sunStart ? sunStart : null,
      sunEnd: showSunHours && sunEnd ? sunEnd : null,
    });
    setSavingPot(false);
    if (pot) goToPlantStep(pot.id);
  }

  function pickPlacement(id: string, suggestedName: string) {
    setPlacement(id);
    if (!potName.trim()) setPotName(suggestedName);
  }

  function finishWithPlant(path: string) {
    if (user) markOnboardingComplete(user.id);
    navigate(path, { replace: true });
  }

  return (
    <div className="screen pad onboarding-screen">
      {step !== "welcome" && (
        <nav className="onboarding-progress" aria-label="Onboarding progress">
          {STEPS.slice(1).map((s, i) => (
            <span
              key={s}
              className={`onboarding-dot${i <= stepIndex - 1 ? " onboarding-dot--active" : ""}${i < stepIndex - 1 ? " onboarding-dot--done" : ""}`}
              aria-hidden="true"
            />
          ))}
          <span className="onboarding-step-label">
            Step {stepIndex} of {STEPS.length - 1}
          </span>
        </nav>
      )}

      {step === "welcome" && (
        <section className="onboarding-step">
          <div className="onboarding-hero-icon" aria-hidden="true">
            <SparkleIcon size={28} />
          </div>
          <h1 className="display onboarding-title">Let&apos;s set up your garden</h1>
          <p className="muted-sm onboarding-lead">
            We&apos;ll get you started in under a minute — where you grow, your first pot,
            and optionally your first plant.
          </p>
          <ul className="onboarding-checklist">
            <li>Set your garden location for weather-aware watering</li>
            <li>Name your first growing spot</li>
            <li>Add a plant by scan, photo, or search</li>
          </ul>
          <Button className="w-full mt-4" onClick={() => setStep("location")}>
            Let&apos;s go
          </Button>
        </section>
      )}

      {step === "location" && (
        <section className="onboarding-step">
          <h1 className="display onboarding-title">Where is your garden?</h1>
          <p className="muted-sm onboarding-lead">
            We use your local weather to adjust watering for rain, heat, and frost near you.
          </p>
          <LocationPicker
            current={location}
            onPick={(loc) => { void setLocation({ location: loc }); }}
          />
          <div className="onboarding-actions">
            <Button variant="secondary" onClick={() => setStep("welcome")}>Back</Button>
            <Button
              disabled={!location}
              onClick={() => setStep("pot")}
            >
              Continue
            </Button>
          </div>
        </section>
      )}

      {step === "pot" && (
        <section className="onboarding-step">
          <h1 className="display onboarding-title">Your first pot</h1>
          <p className="muted-sm onboarding-lead">
            Pots group plants by where they live — each can have its own sun and watering needs.
          </p>

          <div className="grid place-items-center py-4 mb-2 bg-cream-2 rounded-[18px] border border-line">
            <PotIllustration shape="terracotta" plants={[]} size="large" />
          </div>

          <section className="flex flex-col gap-2 mb-4">
            <h3 className="form-h mb-0">Where does it sit?</h3>
            <div className="flex flex-wrap gap-1.5">
              {PLACEMENTS.map((p) => (
                <button
                  key={p.id}
                  type="button"
                  onClick={() => pickPlacement(p.id, p.name)}
                  className={`px-3 py-1.5 rounded-full border text-sm font-sans cursor-pointer transition-colors ${
                    placement === p.id
                      ? "bg-moss-soft border-moss text-moss"
                      : "bg-paper border-line text-ink hover:border-moss-2"
                  }`}
                >
                  {p.label}
                </button>
              ))}
            </div>
          </section>

          <section className="flex flex-col gap-1 mb-4">
            <h3 className="form-h mb-0">Name</h3>
            <input
              className="field-input-bare"
              value={potName}
              onChange={(e) => setPotName(e.target.value)}
              placeholder="e.g. Kitchen herb trio"
            />
          </section>

          <section className="flex flex-col gap-1 mb-4">
            <h3 className="form-h mb-0">Pot size</h3>
            <div className="grid grid-cols-3 gap-1.5 w-full">
              {POT_SIZE_ORDER.map((sz) => {
                const on = potSize === sz;
                return (
                  <button
                    type="button"
                    key={sz}
                    onClick={() => setPotSize(sz)}
                    className={`bg-paper border-[1.5px] rounded-[10px] py-2 px-1.5 cursor-pointer flex flex-col items-center justify-center gap-0.5 min-w-0 w-full ${on ? "border-moss bg-moss-soft" : "border-line"}`}
                  >
                    <span className={`text-[11px] font-medium capitalize ${on ? "text-moss" : "text-ink"}`}>{sz}</span>
                    <span className={`text-[10px] ${on ? "text-moss" : "text-ink-3"}`}>
                      {formatApproxVolumeLiters(sz)}
                    </span>
                  </button>
                );
              })}
            </div>
          </section>

          <section className="flex flex-col gap-1 mb-4">
            {!showSunHours ? (
              <button
                type="button"
                onClick={() => setShowSunHours(true)}
                className="text-sm text-moss font-medium bg-transparent border-0 cursor-pointer p-0 text-left hover:underline"
              >
                + Add sun hours (optional)
              </button>
            ) : (
              <SunHoursPicker
                start={sunStart}
                end={sunEnd}
                onStartChange={setSunStart}
                onEndChange={setSunEnd}
                headingStyle="section"
              />
            )}
          </section>

          <div className="onboarding-actions">
            <Button variant="secondary" onClick={() => setStep("location")}>Back</Button>
            <Button
              disabled={!potName.trim() || sunInvalid || savingPot}
              onClick={() => { void savePotAndContinue(); }}
            >
              {savingPot ? "Creating…" : "Continue"}
            </Button>
          </div>
        </section>
      )}

      {step === "plant" && (
        <section className="onboarding-step">
          <h1 className="display onboarding-title">Add your first plant</h1>
          <p className="muted-sm onboarding-lead">
            Pick how you&apos;d like to enroll — or skip and explore your garden first.
          </p>

          <button
            type="button"
            className={BIG_ACTION_BUTTON}
            onClick={() => finishWithPlant(`/add?potId=${createdPotId}&source=plant`)}
          >
            <span className={BIG_ACTION_ILLU}><CameraScanIllustration /></span>
            <span className="flex-1 flex flex-col gap-0.5">
              <strong className="text-base font-medium">Take a picture</strong>
              <em className="not-italic text-[12.5px] opacity-75 leading-[1.4]">Snap your plant — we&apos;ll try to identify it.</em>
            </span>
            <BackIcon size={18} />
          </button>

          <button
            type="button"
            className={BIG_ACTION_BUTTON}
            onClick={() => finishWithPlant(`/add?potId=${createdPotId}&source=label`)}
          >
            <span className={BIG_ACTION_ILLU}><CareTagIllustration /></span>
            <span className="flex-1 flex flex-col gap-0.5">
              <strong className="text-base font-medium">Scan a care tag</strong>
              <em className="not-italic text-[12.5px] opacity-75 leading-[1.4]">Photo of the label — we fill in care details.</em>
            </span>
            <BackIcon size={18} />
          </button>

          <button
            type="button"
            className={BIG_ACTION_BUTTON}
            onClick={() => finishWithPlant(`/species-search?potId=${createdPotId}`)}
          >
            <span className={BIG_ACTION_ILLU}><SpeciesSearchIllustration /></span>
            <span className="flex-1 flex flex-col gap-0.5">
              <strong className="text-base font-medium">Search species</strong>
              <em className="not-italic text-[12.5px] opacity-75 leading-[1.4]">Look up a plant by name.</em>
            </span>
            <BackIcon size={18} />
          </button>

          <div className="onboarding-actions mt-2">
            <Button variant="secondary" onClick={() => setStep("pot")}>Back</Button>
            <Button variant="secondary" onClick={() => finish()}>Skip for now</Button>
          </div>
        </section>
      )}
    </div>
  );
}
